import React, { useCallback, useEffect, useMemo, useRef, memo, useState } from 'react';
import { getAvatarColor, getInitials } from '../../../utils/avatarUtils';
import { useDispatch, useSelector } from 'react-redux';
import { Spin, Empty, Tooltip } from 'antd';
import {
  LoadingOutlined, PaperClipOutlined,
  MailOutlined, MailFilled,
  FlagOutlined, FlagFilled,
  PushpinOutlined, PushpinFilled,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import relativeTime from 'dayjs/plugin/relativeTime';

import {
  selectThreadsByFolder,
  selectFolderLoadStatus,
  selectFolderPagination,
  fetchThreadsByFolder,
  fetchMoreThreads,
} from '../../../Slices/mailThreadsSlice';
import {
  setSelectedThread,
  selectSelectedThreadId,
  selectSelectedFolderId,
  selectSearch,
  clearSearch,
  setDraftEdit,
} from '../../../Slices/mailUiSlice';
import { selectMessageById } from '../../../Slices/mailMessagesSlice';
import {
  markThreadRead,
  markThreadUnread,
  setThreadFlagged,
  setPinned,
  deleteThread,
} from '../../../Slices/mailThreadsSlice';
import { decrementUnread } from '../../../Slices/mailFoldersSlice';
import {
  markMessageRead,
  updateMessageFlag,
  deleteMessage,
} from '../../../services/graphMailService';

dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ITEM_HEIGHT_NORMAL  = 76;
const ITEM_HEIGHT_COMPACT = 56;
const HEADER_HEIGHT       = 32;
const LOAD_MORE_HEIGHT    = 48;

// ---------------------------------------------------------------------------
// Pure helpers (defined outside — no re-creation on render)
// ---------------------------------------------------------------------------
// getAvatarColor and getInitials imported from utils/avatarUtils

function formatTimestamp(iso) {
  if (!iso) return '';
  const d = dayjs(iso);
  if (d.isToday())     return d.format('h:mm A');
  if (d.isYesterday()) return 'Yesterday';
  if (dayjs().diff(d, 'day') < 7) return d.format('ddd');
  if (dayjs().diff(d, 'year') < 1) return d.format('MMM D');
  return d.format('M/D/YY');
}

function groupThreadsByDate(threads) {
  const today     = [];
  const yesterday = [];
  const older     = [];

  threads.forEach((thread) => {
    const d = dayjs(thread.latestReceivedAt);
    if (d.isToday())     today.push(thread);
    else if (d.isYesterday()) yesterday.push(thread);
    else                 older.push(thread);
  });

  const groups = [];
  if (today.length)     groups.push({ type: 'header', label: 'Today'     }, ...today.map(t => ({ type: 'thread', thread: t })));
  if (yesterday.length) groups.push({ type: 'header', label: 'Yesterday' }, ...yesterday.map(t => ({ type: 'thread', thread: t })));
  if (older.length)     groups.push({ type: 'header', label: 'Older'     }, ...older.map(t => ({ type: 'thread', thread: t })));

  return groups;
}

// ---------------------------------------------------------------------------
// Search helpers
// ---------------------------------------------------------------------------

/** Escape special regex characters in a user-supplied query string. */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Split `text` on `query` (case-insensitive) and return an array of React
 * nodes where each matching segment is wrapped in a <mark> element.
 * Returns the plain string when there is no match or query is empty.
 */
function highlight(text, query) {
  if (!text || !query) return text || '';
  const parts = text.split(new RegExp(`(${escapeRegex(query)})`, 'gi'));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} style={{ background: '#FFF2CC', color: 'inherit', padding: 0, borderRadius: 2 }}>{part}</mark>
      : part
  );
}

// ---------------------------------------------------------------------------
// SearchResultCard — single row in the search results list
// ---------------------------------------------------------------------------

const FONT = "'Segoe UI', Tahoma, sans-serif";

function SearchResultCard({ message, query, isSelected, onClick }) {
  const senderName = (message.from?.name && message.from.name.trim()) || (message.from?.email && message.from.email.trim()) || 'Unknown';
  const date       = formatTimestamp(message.receivedDateTime || message.sentDateTime);

  return (
    <div
      onClick={onClick}
      className="el-thread-row"
      style={{
        display: 'flex', flexDirection: 'column', gap: 2,
        padding: '10px 14px',
        cursor: 'pointer',
        background: isSelected ? '#EBF3FB' : '#fff',
        borderBottom: '1px solid #f3f2f1',
        borderLeft: isSelected ? '3px solid #0f6cbd' : '3px solid transparent',
        transition: 'background .1s',
      }}
    >
      {/* Row 1: sender + date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontFamily: FONT, fontSize: 13, fontWeight: message.isRead ? 400 : 600,
          color: '#242424', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
        }}>
          {highlight(senderName, query)}
        </span>
        <span style={{ fontFamily: FONT, fontSize: 11, color: '#8a8886', flexShrink: 0 }}>{date}</span>
      </div>

      {/* Row 2: subject */}
      <div style={{
        fontFamily: FONT, fontSize: 12, fontWeight: message.isRead ? 400 : 600,
        color: '#242424', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {highlight(message.subject || '(No subject)', query)}
      </div>

      {/* Row 3: body preview */}
      <div style={{
        fontFamily: FONT, fontSize: 12, color: '#605e5c',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {highlight(message.bodyPreview || '', query)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SearchResults — full search results panel (replaces thread list)
// ---------------------------------------------------------------------------

function SearchResults({ search, selectedId, onSelect }) {
  const dispatch   = useDispatch();
  const messageIds = search.results.messageIds;

  const handleClear = () => dispatch(clearSearch());

  // ── States ────────────────────────────────────────────────────────────────

  if (search.phase === 'searching') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 10 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: '#0f6cbd' }} spin />} />
        <span style={{ fontFamily: FONT, fontSize: 13, color: '#605e5c' }}>Searching…</span>
      </div>
    );
  }

  if (search.phase === 'error') {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontFamily: FONT, fontSize: 13, color: '#c50f1f' }}>
              {search.error || 'Search failed. Please try again.'}
            </span>
          }
        />
        <button onClick={handleClear} style={{
          marginTop: 12, fontFamily: FONT, fontSize: 12, color: '#0f6cbd',
          background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline',
        }}>
          Clear search
        </button>
      </div>
    );
  }

  if (search.phase === 'results' && messageIds.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontFamily: FONT, fontSize: 13, color: '#605e5c' }}>
              No results for "{search.committedQuery}"
            </span>
          }
        />
        <button onClick={handleClear} style={{
          marginTop: 12, fontFamily: FONT, fontSize: 12, color: '#0f6cbd',
          background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline',
        }}>
          Clear search
        </button>
      </div>
    );
  }

  if (messageIds.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontFamily: FONT, fontSize: 13, color: '#8a8886' }}>Type to search your mail</span>}
        />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '8px 14px', background: '#faf9f8',
        borderBottom: '1px solid #edebe9',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontFamily: FONT, fontSize: 12, color: '#605e5c' }}>
          {messageIds.length} result{messageIds.length !== 1 ? 's' : ''} for "{search.committedQuery}"
        </span>
        <button onClick={handleClear} style={{
          fontFamily: FONT, fontSize: 12, color: '#0f6cbd',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        }}>
          ✕ Clear
        </button>
      </div>

      {/* Result cards */}
      {messageIds.map(id => (
        <SearchResultCardConnected
          key={id}
          messageId={id}
          query={search.committedQuery}
          isSelected={id === selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

/** Thin connector so SearchResults doesn't need to call useSelector per-card. */
function SearchResultCardConnected({ messageId, query, isSelected, onSelect }) {
  const message = useSelector(state => selectMessageById(state, messageId));
  if (!message) return null;
  return (
    <SearchResultCard
      message={message}
      query={query}
      isSelected={isSelected}
      onClick={() => onSelect(message)}
    />
  );
}

// ---------------------------------------------------------------------------
// RowActionBtn — single icon button in the row action bar
// ---------------------------------------------------------------------------

function RowActionBtn({ icon, title, active, activeColor = '#0f6cbd', onClick }) {
  return (
    <Tooltip title={title} mouseEnterDelay={0.6}>
      <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="row-action-btn"
        style={{
          width: 26, height: 26,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent',
          border: 'none', borderRadius: 4, cursor: 'pointer',
          color: active ? activeColor : '#605e5c',
          fontSize: 13, padding: 0, flexShrink: 0,
        }}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// ThreadRow — memoized, re-renders only when its own data changes
// ---------------------------------------------------------------------------

const ThreadRow = memo(function ThreadRow({
  thread, isSelected, onClick, compact,
  onMarkRead, onFlag, onPin, onDelete,
}) {
  const dispatch = useDispatch();
  const latestMessage = useSelector(state => 
    thread.messageIds?.length > 0 ? selectMessageById(state, thread.messageIds[thread.messageIds.length - 1]) : null
  );
  
  const isDraft = thread.isDraft;
  // Use message sender info if available, otherwise fall back to thread data
  const senderName = latestMessage 
    ? ((latestMessage.from?.name && latestMessage.from.name.trim()) || (latestMessage.from?.email && latestMessage.from.email.trim()) || 'Unknown')
    : ((thread.latestSenderName && thread.latestSenderName.trim()) || thread.participants?.[0] || 'Unknown');
  const initials    = getInitials(senderName);
  const avatarColor = getAvatarColor(senderName);
  const timestamp   = formatTimestamp(thread.latestReceivedAt);
  const isUnread    = thread.hasUnread;
  const isFlagged   = thread.isFlagged;
  const isPinned    = thread.isPinned;
  const height      = compact ? ITEM_HEIGHT_COMPACT : ITEM_HEIGHT_NORMAL;

  return (
    <div
      onClick={onClick}
      style={{
        height, display: 'flex', alignItems: 'center',
        padding: '0 8px 0 12px', gap: 10,
        cursor: 'pointer', userSelect: 'none',
        backgroundColor: isSelected ? '#deecf9' : undefined,
        borderLeft: isSelected ? '3px solid #0f6cbd' : '3px solid transparent',
        borderBottom: '1px solid #f3f2f1',
        fontFamily: "'Segoe UI', Tahoma, sans-serif",
        boxSizing: 'border-box', position: 'relative',
      }}
      className="el-thread-row"
      data-selected={isSelected}
      title={thread.subject}
    >
      {/* Unread dot */}
      {isUnread && (
        <div style={{
          position: 'absolute', left: 0, top: '50%',
          transform: 'translateY(-50%)',
          width: 4, height: 4, borderRadius: '50%',
          background: '#0f6cbd', marginLeft: 6,
        }} />
      )}

      {/* Avatar — hidden for drafts */}
      {!isDraft && (
        <div style={{
          flexShrink: 0, width: 36, height: 36, borderRadius: '50%',
          background: avatarColor, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 600, letterSpacing: 0.3,
        }}>
          {initials}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>

        {isDraft ? (
          /* ── DRAFT layout: [Draft] subject  ·  timestamp ── */
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5,
                overflow: 'hidden', flex: 1, minWidth: 0,
              }}>
                <span style={{
                  color: '#d13438', fontSize: 13, fontWeight: 400,
                  flexShrink: 0,
                }}>
                  Draft
                </span>
                <span style={{
                  fontSize: 13, fontWeight: 400, color: '#242424',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {thread.subject || '(No subject)'}
                </span>
              </div>
              <span className="row-timestamp" style={{
                fontSize: 11, color: '#8a8886', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {timestamp}
              </span>
            </div>
            {!compact && (
              <div style={{
                fontSize: 12, color: '#8a8886', marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {thread.bodyPreview || ''}
              </div>
            )}
          </>
        ) : (
          /* ── NORMAL layout: sender · timestamp / subject / preview ── */
          <>
            {/* Row 1: sender + timestamp */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
              <span style={{
                fontSize: 13, fontWeight: isUnread ? 700 : 400,
                color: '#242424', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
              }}>
                {senderName}
              </span>
              <span className="row-timestamp" style={{
                fontSize: 11,
                color: isUnread ? '#0f6cbd' : '#8a8886',
                whiteSpace: 'nowrap',
                fontWeight: isUnread ? 600 : 400,
                flexShrink: 0,
              }}>
                {timestamp}
              </span>
            </div>

            {/* Row 2: subject */}
            <div style={{
              fontSize: 13, fontWeight: isUnread ? 600 : 400,
              color: '#242424', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              lineHeight: '1.4', marginTop: 1,
            }}>
              {thread.messageCount > 1 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: isUnread ? '#0f6cbd' : '#c8c6c4',
                  color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700,
                  minWidth: 18, height: 16, padding: '0 4px', marginRight: 5,
                  verticalAlign: 'middle',
                }}>
                  {thread.messageCount}
                </span>
              )}
              {thread.subject || '(No subject)'}
            </div>

            {/* Row 3: preview (hidden in compact mode) */}
            {!compact && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                <span style={{
                  fontSize: 12, color: '#8a8886',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', flex: 1,
                }}>
                  {thread.bodyPreview || ''}
                </span>
                {thread.hasAttachments && (
                  <Tooltip title="Has attachments">
                    <PaperClipOutlined style={{ color: '#8a8886', fontSize: 12, flexShrink: 0 }} />
                  </Tooltip>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Row actions — CSS-controlled visibility (no JS hover state) */}
      <div className="row-actions" style={{ display: 'none', alignItems: 'center', gap: 1, flexShrink: 0, marginLeft: 2 }}>
        <RowActionBtn
          icon={isUnread ? <MailFilled /> : <MailOutlined />}
          title={isUnread ? 'Mark as read' : 'Mark as unread'}
          active={isUnread}
          onClick={() => onMarkRead(thread)}
        />
        <RowActionBtn
          icon={isFlagged ? <FlagFilled /> : <FlagOutlined />}
          title={isFlagged ? 'Remove flag' : 'Flag'}
          active={isFlagged}
          activeColor='#ca5010'
          onClick={() => onFlag(thread)}
        />
        <RowActionBtn
          icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />}
          title={isPinned ? 'Unpin' : 'Pin'}
          active={isPinned}
          onClick={() => onPin(thread)}
        />
        <RowActionBtn
          icon={<DeleteOutlined />}
          title="Delete"
          active={false}
          onClick={() => onDelete(thread)}
        />
      </div>
    </div>
  );
}, (prev, next) => (
  prev.isSelected               === next.isSelected               &&
  prev.compact                  === next.compact                  &&
  prev.onMarkRead               === next.onMarkRead               &&
  prev.onFlag                   === next.onFlag                   &&
  prev.onPin                    === next.onPin                    &&
  prev.onDelete                 === next.onDelete                 &&
  prev.thread.conversationId    === next.thread.conversationId    &&
  prev.thread.hasUnread         === next.thread.hasUnread         &&
  prev.thread.isFlagged         === next.thread.isFlagged         &&
  prev.thread.isPinned          === next.thread.isPinned          &&
  prev.thread.messageCount      === next.thread.messageCount      &&
  prev.thread.bodyPreview       === next.thread.bodyPreview       &&
  prev.thread.latestReceivedAt  === next.thread.latestReceivedAt
));

// ---------------------------------------------------------------------------
// DateGroupHeader — memoized
// ---------------------------------------------------------------------------

const DateGroupHeader = memo(function DateGroupHeader({ label }) {
  return (
    <div style={{
      height: HEADER_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      padding: '0 12px',
      fontSize: 11,
      fontWeight: 700,
      color: '#605e5c',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      background: '#faf9f8',
      borderBottom: '1px solid #edebe9',
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
      userSelect: 'none',
      boxSizing: 'border-box',
    }}>
      {label}
    </div>
  );
});

// ---------------------------------------------------------------------------
// LoadMoreRow
// ---------------------------------------------------------------------------

const LoadMoreRow = memo(function LoadMoreRow() {
  return (
    <div style={{
      height: LOAD_MORE_HEIGHT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, color: '#8a8886', gap: 8,
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
      borderTop: '1px solid #edebe9',
    }}>
      <Spin indicator={<LoadingOutlined spin />} size="small" />
      Loading more…
    </div>
  );
});

// ---------------------------------------------------------------------------
// FocusedOtherTabs — pixel-precise Outlook tab bar
// ---------------------------------------------------------------------------

function FocusedOtherTabs({ active, onChange }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'stretch',
      height: 40,
      flexShrink: 0,
      borderBottom: '1px solid #edebe9',
      background: '#fff',
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
    }}>
      {['focused', 'other'].map((tab) => {
        const isActive = active === tab;
        return (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            style={{
              position: 'relative',
              flex: '0 0 50%',
              height: '100%',
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? '#0f6cbd' : '#605e5c',
              outline: 'none',
              userSelect: 'none',
              transition: 'color 0.1s',
            }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#242424'; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#605e5c'; }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {isActive && (
              <span style={{
                position: 'absolute',
                bottom: 0,
                left: '20%',
                right: '20%',
                height: 2,
                background: '#0f6cbd',
                borderRadius: '1px 1px 0 0',
              }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmailList — main component
// ---------------------------------------------------------------------------

export default function EmailList({ compact = false, style }) {
  const dispatch      = useDispatch();
  const containerRef  = useRef(null);

  const folderId      = useSelector(selectSelectedFolderId);
  const selectedId    = useSelector(selectSelectedThreadId);
  const threads       = useSelector((state) => selectThreadsByFolder(state, folderId));
  const loadStatus    = useSelector((state) => selectFolderLoadStatus(state, folderId));
  const pagination    = useSelector((state) => selectFolderPagination(state, folderId));
  const isInbox = useSelector(state => {
    const fid = state.mailUi?.selectedFolderId;
    if (!fid) return false;
    return state.mailFolders?.byId?.[fid]?.wellKnownName === 'inbox';
  });
  const search        = useSelector(selectSearch);
  const isSearchActive = search.query.length > 0 && search.phase !== 'idle';

  const [activeTab, setActiveTab] = useState('focused');
  const prevFolderRef   = useRef(null);
  const fetchingMoreRef = useRef(false); // synchronous guard — prevents duplicate fetches before Redux processes pending

  // Scroll to top on every folder change.
  // Only reset the active tab when navigating AWAY from inbox (not when returning to it).
  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
    const wasInbox = prevFolderRef.current;
    if (wasInbox && !isInbox) setActiveTab('focused');
    prevFolderRef.current = isInbox;
  }, [folderId, isInbox]);

  // Fetch threads when folder changes
  useEffect(() => {
    if (folderId && loadStatus === 'idle') {
      dispatch(fetchThreadsByFolder({ folderId }));
    }
  }, [folderId, loadStatus, dispatch]);

  // Focused/Other tabs only apply to Inbox.
  // Uses Graph's inferenceClassification ('focused' | 'other').
  // Falls back to showing all threads in Focused when field is absent (non-Focused-Inbox mailboxes).
  const filteredThreads = useMemo(() => {
    if (!isInbox) return threads;
    if (activeTab === 'focused') {
      return threads.filter(t => (t.inferenceClassification || 'focused') !== 'other');
    }
    return threads.filter(t => t.inferenceClassification === 'other');
  }, [threads, activeTab, isInbox]);

  // Reset the synchronous guard whenever the folder changes
  useEffect(() => { fetchingMoreRef.current = false; }, [folderId]);

  // Infinite scroll — only depends on folderId; reads cursor/hasMore from refs
  // so the handler is never stale and the effect never re-runs mid-fetch.
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const { cursor, hasMore } = paginationRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 40 && hasMore && cursor && !fetchingMoreRef.current) {
        fetchingMoreRef.current = true;
        dispatch(fetchMoreThreads({ folderId, cursor }))
          .finally(() => { fetchingMoreRef.current = false; });
      }
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [folderId, dispatch]); // eslint-disable-line

  const handleSelectThread = useCallback((thread) => {
    // Draft: intercept and open in compose instead of reading pane
    if (thread.isDraft) {
      const msgId = thread.messageIds?.[thread.messageIds.length - 1] ?? null;
      if (msgId) dispatch(setDraftEdit(msgId));
      return;
    }

    const lastMsgId = thread.messageIds?.[thread.messageIds.length - 1] ?? null;
    dispatch(setSelectedThread({
      conversationId: thread.conversationId,
      latestMessageId: lastMsgId,
    }));
    // Mark as read on click (optimistic + API sync)
    if (thread.hasUnread) {
      dispatch(markThreadRead({ conversationId: thread.conversationId }));
      dispatch(decrementUnread({ folderId: thread.folderId, by: thread.unreadCount || 1 }));
      if (lastMsgId) {
        markMessageRead(lastMsgId, true).catch(() => {
          dispatch(markThreadUnread({ conversationId: thread.conversationId }));
        });
      }
    }
  }, [dispatch]);

  // Row action handlers — optimistic update then async API call
  const handleMarkRead = useCallback((thread) => {
    const nowUnread = thread.hasUnread;
    if (nowUnread) {
      dispatch(markThreadRead({ conversationId: thread.conversationId }));
      dispatch(decrementUnread({ folderId: thread.folderId, by: thread.unreadCount || 1 }));
    } else {
      dispatch(markThreadUnread({ conversationId: thread.conversationId }));
    }
    const latestId = thread.messageIds?.[thread.messageIds.length - 1];
    if (latestId) markMessageRead(latestId, !nowUnread).catch(() => {
      if (nowUnread) dispatch(markThreadUnread({ conversationId: thread.conversationId }));
      else dispatch(markThreadRead({ conversationId: thread.conversationId }));
    });
  }, [dispatch]);

  const handleFlag = useCallback((thread) => {
    const nowFlagged = thread.isFlagged;
    const newStatus  = nowFlagged ? 'notFlagged' : 'flagged';
    dispatch(setThreadFlagged({ conversationId: thread.conversationId, isFlagged: !nowFlagged }));
    const latestId = thread.messageIds?.[thread.messageIds.length - 1];
    if (latestId) updateMessageFlag(latestId, newStatus).catch(() => {
      dispatch(setThreadFlagged({ conversationId: thread.conversationId, isFlagged: nowFlagged }));
    });
  }, [dispatch]);

  const handlePin = useCallback((thread) => {
    dispatch(setPinned({ conversationId: thread.conversationId, isPinned: !thread.isPinned }));
  }, [dispatch]);

  const handleDelete = useCallback((thread) => {
    dispatch(deleteThread({ conversationId: thread.conversationId, folderId: thread.folderId }));
    const latestId = thread.messageIds?.[thread.messageIds.length - 1];
    if (latestId) deleteMessage(latestId).catch(() => {});
  }, [dispatch]);


  // ---------------------------------------------------------------------------
  const groups = useMemo(() => {
    const pinned   = filteredThreads.filter(t => t.isPinned)
                       .sort((a, b) => (b.pinnedAt || 0) - (a.pinnedAt || 0));
    const unpinned = filteredThreads.filter(t => !t.isPinned);
    const result   = [];
    if (pinned.length) {
      result.push({ type: 'header', label: 'Pinned' });
      pinned.forEach(t => result.push({ type: 'thread', thread: t }));
    }
    result.push(...groupThreadsByDate(unpinned));
    return result;
  }, [filteredThreads]);

  // Loading state
  // ---------------------------------------------------------------------------

  // Single render — scroll div always mounts so containerRef is always set
  const showSpinner   = loadStatus === 'loading';
  const showError     = loadStatus === 'error';
  const showEmpty     = !folderId || (loadStatus === 'ready' && filteredThreads.length === 0);

  return (
    <>
      <style>{hoverCss}</style>
      <div style={containerStyle(style)}>
        {isInbox && !isSearchActive && <FocusedOtherTabs active={activeTab} onChange={setActiveTab} />}

        {/* Scroll container — always mounted so ref + listener are stable */}
        <div ref={containerRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

          {/* SEARCH MODE */}
          {isSearchActive && (
            <SearchResults
              search={search}
              selectedId={selectedId}
              onSelect={(message) => {
                dispatch(setSelectedThread({
                  conversationId: message.conversationId,
                  latestMessageId: message.id,
                }));
                if (!message.isRead) {
                  markMessageRead(message.id, true).catch(() => {});
                }
              }}
            />
          )}

          {/* NORMAL MODE (folder view) */}
          {!isSearchActive && (
            <>
              {!folderId && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<span style={emptyTextStyle}>Select a folder</span>}
                  style={{ margin: 'auto', paddingTop: 60 }} />
              )}

              {showSpinner && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: '#0f6cbd' }} spin />} />
                </div>
              )}

              {showError && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<span style={{ ...emptyTextStyle, color: '#c50f1f' }}>Failed to load. Please try again.</span>}
                  style={{ margin: 'auto', paddingTop: 60 }} />
              )}

              {!showSpinner && !showError && showEmpty && folderId && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<span style={emptyTextStyle}>{activeTab === 'focused' ? 'No messages' : 'Nothing in Other'}</span>}
                  style={{ margin: 'auto', paddingTop: 60 }} />
              )}

              {!showSpinner && !showError && !showEmpty && groups.map((item, idx) => {
                if (item.type === 'header') {
                  return <DateGroupHeader key={`h-${idx}`} label={item.label} />;
                }
                const { thread } = item;
                return (
                  <ThreadRow
                    key={thread.conversationId}
                    thread={thread}
                    isSelected={thread.conversationId === selectedId}
                    compact={compact}
                    onClick={() => handleSelectThread(thread)}
                    onMarkRead={handleMarkRead}
                    onFlag={handleFlag}
                    onPin={handlePin}
                    onDelete={handleDelete}
                  />
                );
              })}

              {pagination.isFetchingMore && (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 18, color: '#0f6cbd' }} spin />} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function containerStyle(extra) {
  return {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    background: '#fff',
    borderRight: '1px solid #edebe9',
    overflow: 'hidden',
    ...extra,
  };
}

const emptyTextStyle = {
  color: '#8a8886',
  fontSize: 13,
  fontFamily: "'Segoe UI', Tahoma, sans-serif",
};

// Hover styles via a <style> tag (keeps inline styles clean)
const hoverCss = `
  .el-thread-row:hover {
    background-color: #f3f2f1 !important;
  }
  .el-thread-row[data-selected="true"]:hover {
    background-color: #deecf9 !important;
  }

  /* Show actions, hide timestamp on hover or selected */
  .el-thread-row:hover .row-actions,
  .el-thread-row[data-selected="true"] .row-actions {
    display: flex !important;
  }
  .el-thread-row:hover .row-timestamp,
  .el-thread-row[data-selected="true"] .row-timestamp {
    display: none !important;
  }

  /* Action button hover */
  .row-action-btn:hover {
    background-color: #e8e6e4 !important;
  }
`;
